<script setup>
import { computed, h } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { provideSidebarContext } from 'dashboard/components-next/sidebar/provider';
import { useStorage } from '@vueuse/core';

import SidebarGroup from 'dashboard/components-next/sidebar/SidebarGroup.vue';
import ChannelLeaf from 'dashboard/components-next/sidebar/ChannelLeaf.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();

const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const inboxes = useMapGetter('inboxes/getInboxes');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

const sortedInboxes = computed(() => {
  return [...(inboxes.value || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

// Expanded item state management
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

// Define section configurations
const sectionConfigs = computed(() => ({
  conversations: {
    name: 'Conversation',
    label: t('SIDEBAR.CONVERSATIONS'),
    icon: 'i-lucide-message-circle',
    children: [
      {
        name: 'All',
        label: t('SIDEBAR.ALL_CONVERSATIONS'),
        activeOn: ['inbox_conversation'],
        to: accountScopedRoute('home'),
      },
      {
        name: 'Mentions',
        label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
        activeOn: ['conversation_through_mentions'],
        to: accountScopedRoute('conversation_mentions'),
      },
      {
        name: 'Unattended',
        activeOn: ['conversation_through_unattended'],
        label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
        to: accountScopedRoute('conversation_unattended'),
      },
      {
        name: 'Folders',
        label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
        icon: 'i-lucide-folder',
        activeOn: ['conversations_through_folders'],
        children: conversationCustomViews.value.map(view => ({
          name: `${view.name}-${view.id}`,
          label: view.name,
          to: accountScopedRoute('folder_conversations', { id: view.id }),
        })),
      },
      {
        name: 'Teams',
        label: t('SIDEBAR.TEAMS'),
        icon: 'i-lucide-users',
        activeOn: ['conversations_through_team'],
        children: teams.value.map(team => ({
          name: `${team.name}-${team.id}`,
          label: team.name,
          to: accountScopedRoute('team_conversations', { teamId: team.id }),
        })),
      },
      {
        name: 'Channels',
        label: t('SIDEBAR.CHANNELS'),
        icon: 'i-lucide-mailbox',
        activeOn: ['conversation_through_inbox'],
        children: sortedInboxes.value.map(inbox => ({
          name: `${inbox.name}-${inbox.id}`,
          label: inbox.name,
          to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
          component: leafProps =>
            h(ChannelLeaf, {
              label: leafProps.label,
              active: leafProps.active,
              inbox,
            }),
        })),
      },
      {
        name: 'Labels',
        label: t('SIDEBAR.LABELS'),
        icon: 'i-lucide-tag',
        activeOn: ['conversations_through_label'],
        children: labels.value.map(label => ({
          name: `${label.title}-${label.id}`,
          label: label.title,
          icon: h('span', {
            class:
              'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
            style: { backgroundColor: label.color },
          }),
          to: accountScopedRoute('label_conversations', {
            label: label.title,
          }),
        })),
      },
    ],
    routes: [
      'home',
      'inbox_dashboard',
      'inbox_conversation',
      'label_conversations',
      'team_conversations',
      'folder_conversations',
      'conversation_mentions',
      'conversation_unattended',
      'conversation_through_inbox',
      'conversations_through_folders',
      'conversations_through_team',
      'conversations_through_label',
    ],
  },
  contacts: {
    name: 'Contacts',
    label: t('SIDEBAR.CONTACTS'),
    icon: 'i-lucide-contact',
    children: [
      {
        name: 'All Contacts',
        label: t('SIDEBAR.ALL_CONTACTS'),
        to: accountScopedRoute(
          'contacts_dashboard_index',
          {},
          { page: 1, search: undefined }
        ),
        activeOn: ['contacts_dashboard_index', 'contacts_edit'],
      },
      {
        name: 'Active',
        label: t('SIDEBAR.ACTIVE'),
        to: accountScopedRoute('contacts_dashboard_active'),
        activeOn: ['contacts_dashboard_active'],
      },
      {
        name: 'Segments',
        icon: 'i-lucide-group',
        label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
        children: contactCustomViews.value.map(view => ({
          name: `${view.name}-${view.id}`,
          label: view.name,
          to: accountScopedRoute(
            'contacts_dashboard_segments_index',
            { segmentId: view.id },
            { page: 1 }
          ),
          activeOn: [
            'contacts_dashboard_segments_index',
            'contacts_edit_segment',
          ],
        })),
      },
    ],
    routes: [
      'contacts_dashboard_index',
      'contacts_dashboard_active',
      'contacts_dashboard_segments_index',
      'contacts_dashboard_labels_index',
      'contacts_edit',
      'contacts_edit_segment',
      'contacts_edit_label',
    ],
  },
  reports: {
    name: 'Reports',
    label: t('SIDEBAR.REPORTS'),
    icon: 'i-lucide-chart-spline',
    children: [
      {
        name: 'Report Overview',
        label: t('SIDEBAR.REPORTS_OVERVIEW'),
        to: accountScopedRoute('account_overview_reports'),
      },
      {
        name: 'Report Conversation',
        label: t('SIDEBAR.REPORTS_CONVERSATION'),
        to: accountScopedRoute('conversation_reports'),
      },
      {
        name: 'Reports Agent',
        label: t('SIDEBAR.REPORTS_AGENT'),
        to: accountScopedRoute('agent_reports'),
      },
      {
        name: 'Reports Label',
        label: t('SIDEBAR.REPORTS_LABEL'),
        to: accountScopedRoute('label_reports'),
      },
      {
        name: 'Reports Inbox',
        label: t('SIDEBAR.REPORTS_INBOX'),
        to: accountScopedRoute('inbox_reports'),
      },
      {
        name: 'Reports Team',
        label: t('SIDEBAR.REPORTS_TEAM'),
        to: accountScopedRoute('team_reports'),
      },
      {
        name: 'Reports CSAT',
        label: t('SIDEBAR.CSAT'),
        to: accountScopedRoute('csat_reports'),
      },
      {
        name: 'Reports SLA',
        label: t('SIDEBAR.REPORTS_SLA'),
        to: accountScopedRoute('sla_reports'),
      },
      {
        name: 'Reports Bot',
        label: t('SIDEBAR.REPORTS_BOT'),
        to: accountScopedRoute('bot_reports'),
      },
    ],
    routes: [
      'account_overview_reports',
      'conversation_reports',
      'agent_reports',
      'label_reports',
      'inbox_reports',
      'team_reports',
      'csat_reports',
      'sla_reports',
      'bot_reports',
    ],
  },
  settings: {
    name: 'Settings',
    label: t('SIDEBAR.SETTINGS'),
    icon: 'i-lucide-bolt',
    children: [
      {
        name: 'Settings Account Settings',
        label: t('SIDEBAR.ACCOUNT_SETTINGS'),
        to: accountScopedRoute('general_settings_index'),
      },
      {
        name: 'Settings Agents',
        label: t('SIDEBAR.AGENTS'),
        to: accountScopedRoute('agent_list'),
      },
      {
        name: 'Settings Teams',
        label: t('SIDEBAR.TEAMS'),
        to: accountScopedRoute('settings_teams_list'),
      },
      {
        name: 'Settings Inboxes',
        label: t('SIDEBAR.INBOXES'),
        to: accountScopedRoute('settings_inbox_list'),
      },
      {
        name: 'Settings Labels',
        label: t('SIDEBAR.LABELS'),
        to: accountScopedRoute('labels_list'),
      },
      {
        name: 'Settings Custom Attributes',
        label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
        to: accountScopedRoute('attributes_list'),
      },
      {
        name: 'Settings Automation',
        label: t('SIDEBAR.AUTOMATION'),
        to: accountScopedRoute('automation_list'),
      },
      {
        name: 'Settings Macros',
        label: t('SIDEBAR.MACROS'),
        to: accountScopedRoute('macros_wrapper'),
      },
      {
        name: 'Settings Canned Responses',
        label: t('SIDEBAR.CANNED_RESPONSES'),
        to: accountScopedRoute('canned_list'),
      },
      {
        name: 'Settings Integrations',
        label: t('SIDEBAR.INTEGRATIONS'),
        to: accountScopedRoute('settings_applications'),
      },
    ],
    routes: [
      'general_settings_index',
      'agent_list',
      'settings_teams_list',
      'settings_inbox_list',
      'labels_list',
      'attributes_list',
      'automation_list',
      'macros_wrapper',
      'canned_list',
      'settings_applications',
      'auditlogs_list',
    ],
  },
}));

// Determine which section to show based on current route
const currentSection = computed(() => {
  const routeName = route.name;
  if (!routeName) return null;

  for (const [key, config] of Object.entries(sectionConfigs.value)) {
    if (
      config.routes.some(
        r => routeName === r || routeName?.startsWith(r.replace('_index', ''))
      )
    ) {
      return config;
    }
  }
  return null;
});

const showSidebar = computed(() => currentSection.value !== null);
</script>

<template>
  <aside
    v-if="showSidebar"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm py-2 w-[200px] flex-shrink-0 h-full overflow-hidden"
  >
    <!-- Section header -->
    <div class="px-3 py-2 mb-2">
      <div class="flex items-center gap-2">
        <span :class="[currentSection.icon, 'size-4 text-n-slate-11']" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ currentSection.label }}
        </span>
      </div>
    </div>

    <!-- Navigation items -->
    <nav class="flex-1 overflow-y-auto px-2 no-scrollbar">
      <ul class="flex flex-col gap-1 m-0 list-none">
        <SidebarGroup
          v-for="item in currentSection.children"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
  </aside>
</template>
